import * as React from 'react';
import { WarningLine } from '@mingcute/react';

import { cn } from '../lib/cn';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import { Button } from './button';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'danger';
  confirmLabel?: string;
  cancelLabel?: string;
  /** When set, user must type this exact phrase to enable confirm. */
  typedConfirmPhrase?: string;
  typedConfirmLabel?: string;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  variant = 'default',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  typedConfirmPhrase,
  typedConfirmLabel,
  onConfirm,
  loading,
}: ConfirmDialogProps) {
  const [typed, setTyped] = React.useState('');

  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);

  const isTypedRequired = Boolean(typedConfirmPhrase);
  const typedOk = !isTypedRequired || typed === typedConfirmPhrase;
  const isDanger = variant === 'danger';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {isDanger ? (
              <WarningLine size={20} className="text-[var(--color-danger)]" aria-hidden />
            ) : null}
            <DialogTitle>{title}</DialogTitle>
          </div>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>
        {isTypedRequired ? (
          <div className="flex flex-col gap-2">
            <label className="text-xs text-[var(--color-fg-secondary)]">
              {typedConfirmLabel ?? (
                <>
                  Type <span className="font-mono font-semibold">{typedConfirmPhrase}</span> to confirm
                </>
              )}
            </label>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              autoFocus
              className={cn(
                'h-9 w-full rounded-[var(--radius-md)] border bg-[var(--color-bg)] px-3 text-sm',
                'border-[var(--color-border)] text-[var(--color-fg)]',
                'focus-visible:outline-none',
              )}
            />
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button
            variant={isDanger ? 'danger' : 'primary'}
            onClick={() => onConfirm()}
            disabled={!typedOk || loading}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
